// combatpro/panels/IntroOverlay.js
//
// Combat PRO - IntroOverlay. The Combat PRO start screen: an animated opening built from
// the owner-approved Combat PRO launch artwork set (visual-direction-v2-20260927,
// proofs/combat-pro), followed by a hold on the finished composition with the single
// Enter System control. Replaces the previous dragon.mp4 start video.
//
// How it works:
//   - The artwork is a 3840x2160 layered set described by motion-element-manifest.json.
//     Web copies live in public/combatpro-intro/ (derived, optimised WebP; the source
//     artwork is untouched).
//   - Everything is laid out on a virtual 3840x2160 "stage" (percent positions), sized
//     to the window so the whole safe area (panels and title) stays visible on desktop,
//     widescreen and TV; any spare edge is filled by a soft, out-of-focus extension of the
//     battle scene that the stage feathers into (no panels are ever duplicated).
//   - The opening follows the manifest timeline (3.5s): the battle scene fades up under
//     a slow camera push, the dragon threat light and counterspell flare bloom, and each
//     command panel is revealed from the finished master in manifest order. The full
//     master then settles in, so the final frame is the master exactly, and the scene
//     keeps a very slow breathing drift while it waits.
//   - Phones / portrait tablets get their own framing: the scene fills the screen and the
//     Combat PRO title panel from the same master is presented on its own.
//   - prefers-reduced-motion: the finished master is shown statically, no motion.
//   - Enter System is usable at all times (it never waits for the opening), then fades
//     out over 310ms and unmounts, exactly as before.
//
// It mounts INSIDE the authenticated Combat PRO app, so it never runs before or interferes
// with DCC authentication / the ?dcc= bootstrap. No combat state, no getElementById, no
// optional chaining, no em dash. Presentation lives in combatProTheme.css (.cp-root).

import React, { useState, useEffect } from 'react';

// Same origin convention as the previous start video (served by the portal deploy).
export const INTRO_ASSET_BASE = 'https://app.visinvoke.com/combatpro-intro/';

const STAGE_W = 3840;
const STAGE_H = 2160;
// Region that must stay visible in landscape: every panel plus the title (from the manifest).
const SAFE_W = 3600;
const SAFE_H = 1920;

// Command panels, revealed from the finished master in manifest order.
// [id, x, y, w, h, entrance, start, end]
export const INTRO_PANELS = [
  ['combat-brand-panel', 2890, 1745, 811, 296, 'fade', 0.12, 0.42],
  ['initiative-panel', 120, 190, 731, 941, 'reveal', 0.28, 0.62],
  ['dragon-status-panel', 1010, 120, 1251, 401, 'reveal', 0.58, 0.88],
  ['tactical-state-panel', 2940, 170, 761, 841, 'reveal', 0.92, 1.22],
  ['encounter-log-panel', 2920, 1120, 781, 561, 'reveal', 1.35, 1.68],
  ['attack-roll-panel', 900, 1320, 551, 341, 'pulse', 1.72, 2.05],
  ['encounter-control-panel', 1510, 1830, 1321, 196, 'reveal', 2.02, 2.35]
];

// Panel content lighting up as each state resolves (screen-blended over the panel).
export const INTRO_CONTENT = [
  ['initiative-content', 206, 261, 563, 806, 0.42, 1.2],
  ['dragon-status-content', 1100, 194, 809, 276, 0.7, 1.28],
  ['tactical-state-content', 3021, 246, 513, 663, 1.05, 1.82],
  ['encounter-log-content', 3001, 1196, 487, 411, 1.5, 2.15],
  ['attack-roll-content', 1028, 1380, 295, 227, 1.88, 2.3],
  ['encounter-control-content', 1602, 1894, 868, 96, 2.18, 2.62]
];

const THREAT = [1097, 98, 2137, 1645];
const SPELL = [2392, 864, 347, 343];
const BRAND = [2890, 1745, 811, 296];

function pct(n, d) { return (n / d * 100) + '%'; }

function boxStyle(x, y, w, h) {
  return { left: pct(x, STAGE_W), top: pct(y, STAGE_H), width: pct(w, STAGE_W), height: pct(h, STAGE_H) };
}

// A crop of the master image showing exactly the stage box (x, y, w, h).
function cropStyle(src, x, y, w, h) {
  return {
    backgroundImage: 'url(' + src + ')',
    backgroundSize: pct(STAGE_W, w) + ' ' + pct(STAGE_H, h),
    backgroundPosition: pct(x, STAGE_W - w) + ' ' + pct(y, STAGE_H - h)
  };
}

function timing(start, end) {
  return { animationDelay: start + 's', animationDuration: Math.max(0.2, end - start) + 's' };
}

// Stage geometry for a window size. Landscape: as large as possible while the safe area
// stays fully visible. Portrait: the scene fills the screen (own framing, see CSS).
export function computeIntroLayout(vw, vh) {
  var W = vw > 0 ? vw : 1920;
  var H = vh > 0 ? vh : 1080;
  var portrait = W / H < 1;
  var cover = Math.max(W / STAGE_W, H / STAGE_H);
  var s = portrait ? cover : Math.min(cover, W / SAFE_W, H / SAFE_H);
  var sw = STAGE_W * s;
  var sh = STAGE_H * s;
  var focusX = portrait ? 0.52 : 0.5;
  return {
    portrait: portrait,
    left: W / 2 - sw * focusX,
    top: (H - sh) / 2,
    width: sw,
    height: sh,
    gapX: sw < W - 1,
    gapY: sh < H - 1,
    hiRes: Math.max(W, H) * (typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1) > 2200
  };
}

function viewport() {
  if (typeof window === 'undefined') { return [1920, 1080]; }
  return [window.innerWidth || 1920, window.innerHeight || 1080];
}

function IntroOverlay() {
  const [visible, setVisible] = useState(true);
  const [closing, setClosing] = useState(false);
  const [ready, setReady] = useState(false);
  const [layout, setLayout] = useState(function () { var v = viewport(); return computeIntroLayout(v[0], v[1]); });

  // Asset resolution is chosen once per mount (no re-downloads on resize).
  const [res] = useState(function () { return layout.hiRes ? '3840' : '1920'; });
  const envSrc = INTRO_ASSET_BASE + 'cp-env-' + res + '.webp';
  const masterSrc = INTRO_ASSET_BASE + 'cp-master-' + res + '.webp';

  useEffect(function () {
    function onResize() { var v = viewport(); setLayout(computeIntroLayout(v[0], v[1])); }
    window.addEventListener('resize', onResize);
    return function () { window.removeEventListener('resize', onResize); };
  }, []);

  // Start the opening only once the scene and master are decoded, so it never begins on a
  // blank or half-loaded frame. A slow connection starts it anyway after 4s.
  useEffect(function () {
    var done = false;
    var pending = 2;
    function go() { if (!done) { done = true; setReady(true); } }
    function one() { pending -= 1; if (pending <= 0) { go(); } }
    [envSrc, masterSrc].forEach(function (src) {
      var img = new window.Image();
      img.onload = function () {
        if (img.decode) { img.decode().then(one, one); } else { one(); }
      };
      img.onerror = one;
      img.src = src;
    });
    var t = setTimeout(go, 4000);
    return function () { done = true; clearTimeout(t); };
  }, [envSrc, masterSrc]);

  if (!visible) { return null; }

  function enterSystem() {
    setClosing(true);
    setTimeout(function () { setVisible(false); }, 310);
  }

  // Enter System sits in the dark lower-left of the artwork, kept fully on screen.
  var vpW = viewport()[0];
  var btnLeft = Math.min(Math.max(layout.left + layout.width * 485 / STAGE_W, 136), vpW - 136);
  var btnTop = layout.top + layout.height * 1985 / STAGE_H;

  var stageStyle = { left: layout.left + 'px', top: layout.top + 'px', width: layout.width + 'px', height: layout.height + 'px' };
  var cls = 'cpi' + (ready ? ' cpi-run' : '') + (layout.portrait ? ' cpi-portrait' : ' cpi-landscape') +
    (layout.gapX ? ' cpi-gap-x' : '') + (layout.gapY ? ' cpi-gap-y' : '');

  return (
    <div id="vi-intro-overlay" className={cls} style={{ display: 'block', opacity: closing ? 0 : 1 }}>
      <div className="cpi-camera">
        <div className="cpi-backdrop" aria-hidden="true">
          <img className="cpi-env" src={envSrc} alt="" draggable="false" />
        </div>
        <div className="cpi-stage" style={stageStyle} aria-hidden="true">
          <img className="cpi-env" src={envSrc} alt="" draggable="false" />
          <img className="cpi-glow cpi-threat" src={INTRO_ASSET_BASE + 'cp-dragon-threat-light.webp'} alt="" draggable="false"
            style={boxStyle(THREAT[0], THREAT[1], THREAT[2], THREAT[3])} />
          <img className="cpi-glow cpi-spell" src={INTRO_ASSET_BASE + 'cp-counterspell-impact.webp'} alt="" draggable="false"
            style={boxStyle(SPELL[0], SPELL[1], SPELL[2], SPELL[3])} />
          {INTRO_PANELS.map(function (p) {
            return (
              <div key={p[0]} className={'cpi-panel cpi-in-' + p[5]}
                style={Object.assign({}, boxStyle(p[1], p[2], p[3], p[4]), cropStyle(masterSrc, p[1], p[2], p[3], p[4]), timing(p[6], p[7]))} />
            );
          })}
          {INTRO_CONTENT.map(function (c) {
            return (
              <img key={c[0]} className="cpi-glow cpi-content" src={INTRO_ASSET_BASE + 'cp-' + c[0] + '.webp'} alt="" draggable="false"
                style={Object.assign({}, boxStyle(c[1], c[2], c[3], c[4]), timing(c[5], c[6] + 0.5))} />
            );
          })}
          <img className="cpi-master" src={masterSrc} alt="" draggable="false" />
          <div className="cpi-enter-anchor" />
        </div>
      </div>
      <div className="cpi-brand" aria-hidden="true" style={cropStyle(masterSrc, BRAND[0], BRAND[1], BRAND[2], BRAND[3])} />
      <div className={'vi-intro-actions' + (layout.portrait ? '' : ' cpi-actions-stage')}
        style={layout.portrait ? undefined : { left: btnLeft + 'px', top: btnTop + 'px' }}>
        <button id="vi-intro-enter" onClick={enterSystem}>{'⚔️ Enter System'}</button>
      </div>
    </div>
  );
}

export default IntroOverlay;
